/*
  Playvo interactive components. Both are progressive enhancements:
  the bat finder is a real <form> that still submits without JS, and the
  pincode box is hidden from no-JS users by the section markup.
*/

/**
 * Guided finders (gear finder on the homepage, bat finder on the cricket
 * collection). Each <select> option carries `data-params` — a query fragment
 * such as "filter.p.tag=short-handle" — and may carry `data-url`, a collection
 * to land on. We concatenate the chosen fragments onto the chosen collection so
 * Shopify's native storefront filtering does the actual work. No app, no custom
 * search index.
 *
 * The gear finder's first question (sport) is what sets `data-url`; every other
 * question only contributes filters. That's the whole difference between the two.
 */
class PlayvoFinder extends HTMLElement {
  connectedCallback() {
    this.form = this.querySelector('form');
    if (!this.form) return;
    this.form.addEventListener('submit', this.onSubmit.bind(this));
  }

  /** The last chosen option that names a collection wins; otherwise the default. */
  destination() {
    let base = this.dataset.collectionUrl || '/collections/all';
    const fragments = [];

    this.querySelectorAll('select').forEach((select) => {
      const option = select.selectedOptions[0];
      if (!option) return;

      if (option.dataset.url) base = option.dataset.url;
      if (option.dataset.params) fragments.push(option.dataset.params);
    });

    return fragments.length ? `${base}?${fragments.join('&')}` : base;
  }

  onSubmit(event) {
    event.preventDefault();
    window.location.href = this.destination();
  }
}

customElements.define('playvo-gear-finder', PlayvoFinder);
customElements.define('playvo-bat-finder', class extends PlayvoFinder {});

/**
 * Pincode delivery check.
 *
 * This is a front-end estimate driven by pincode prefixes configured in the
 * theme editor — it does not call a courier API. It exists to answer the
 * question Indian shoppers ask before adding to cart ("will this reach me,
 * and can I pay cash?"), which measurably lifts conversion.
 *
 * To make it authoritative, swap `lookup()` for a fetch to your courier's
 * serviceability endpoint (Shiprocket / Delhivery / Blue Dart) via a Shopify
 * app proxy — the DOM contract below stays identical.
 */
class PlayvoPincodeCheck extends HTMLElement {
  connectedCallback() {
    this.input = this.querySelector('.playvo-pin__input');
    this.result = this.querySelector('.playvo-pin__result');
    this.button = this.querySelector('.playvo-pin__button');
    if (!this.input || !this.result || !this.button) return;

    this.metroPrefixes = this.parseList(this.dataset.metroPrefixes);
    this.codBlocked = this.parseList(this.dataset.codBlockedPrefixes);
    this.unserviceable = this.parseList(this.dataset.unserviceablePrefixes);

    this.button.addEventListener('click', () => this.check());
    this.input.addEventListener('keydown', (event) => {
      if (event.key === 'Enter') {
        event.preventDefault();
        this.check();
      }
    });
  }

  parseList(value) {
    return (value || '')
      .split(',')
      .map((entry) => entry.trim())
      .filter(Boolean);
  }

  matches(pincode, prefixes) {
    return prefixes.some((prefix) => pincode.startsWith(prefix));
  }

  check() {
    const pincode = this.input.value.trim();

    if (!/^\d{6}$/.test(pincode)) {
      this.render('error', 'Enter a valid 6-digit pincode.');
      return;
    }

    const verdict = this.lookup(pincode);
    this.render(verdict.state, verdict.message);
  }

  lookup(pincode) {
    if (this.matches(pincode, this.unserviceable)) {
      return {
        state: 'error',
        message: 'We don’t deliver to this pincode yet. Write to us and we’ll try to arrange it.',
      };
    }

    const isMetro = this.matches(pincode, this.metroPrefixes);
    const days = isMetro ? this.dataset.metroDays || '2–3' : this.dataset.standardDays || '4–6';
    const cod = this.matches(pincode, this.codBlocked)
      ? 'Prepaid only (UPI, cards, netbanking) at this pincode.'
      : 'Cash on delivery available.';

    return {
      state: 'ok',
      message: `<strong>Delivers in ${days} working days</strong> to ${pincode}. ${cod}`,
    };
  }

  render(state, message) {
    this.result.dataset.state = state;
    this.result.innerHTML = message;
  }
}

customElements.define('playvo-pincode-check', PlayvoPincodeCheck);
